/**
 * Client-side model for the per-hotel media subcollection (`hotels/{hotelId}/media/{id}`).
 * The `gallery` doc holds a hotel's FULL photo set (the head doc's `images` carries only the
 * first ~10 for cards). Read lazily — subscribe only when the user is about to browse the
 * gallery, so chat threads with many hotel widgets don't pay for large docs they never open.
 */

import type { WidgetImage } from '@elara/domain/models/schemas/image-schemas';

import type { FirestoreContainer } from '#containers/FirestoreContainer.js';
import type { ClientInstanceOf } from '#firebase/types.js';

import { HotelMediaModelSpec } from '@elara/domain/models/HotelMediaModelSpec';

import { createClientModel } from '#firebase/createClientModel.js';
import { resolveMediaUrl } from '#lib/media-url.js';

export function createClientHotelMediaModel(firestore: FirestoreContainer) {
  return createClientModel(firestore, HotelMediaModelSpec, () => ({
    /**
     * Gallery images in card-compatible shape. The display URL prefers the `large` rendition,
     * then `original`, then the stored canonical `url`. This deliberately diverges from the
     * server's `CANONICAL_VARIANT_ORDER` (`original` first, in sabre/utils.ts), which picks the
     * highest-fidelity URL to STORE as the canonical; here we pick the best URL to DISPLAY —
     * `large` is sized for the gallery viewer, while `original` can be a multi-thousand-pixel
     * asset that wastes bandwidth on screen.
     */
    get widgetImages(): WidgetImage[] {
      return (this.images ?? []).map((image) => ({
        id: image.id,
        url: resolveMediaUrl(image.variants?.large?.url ?? image.variants?.original?.url ?? image.url),
        caption: image.caption,
        attribution: image.attribution,
        source: image.source,
        alt: image.alt,
        roomTypeCodes: image.roomTypeCodes,
        tags: image.tags,
        priority: image.priority,
      }));
    },
  }));
}

export type ClientHotelMediaModel = ReturnType<typeof createClientHotelMediaModel>;
export type HotelMediaGallery = ClientInstanceOf<ClientHotelMediaModel>;
